import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { getTrip } from '../../../services/firestore/trips';
import { getHostProfile } from '../../../services/firestore/hosts';
import { getUserData } from '../../../services/auth';
import { createBooking } from '../../../services/firestore/bookings';
import { useAuth } from '../../../contexts/AuthContext';
import { hasRole } from '../../../services/auth';
import DatePicker from '../components/DatePicker';
import InquiryBookingModal from '../components/InquiryBookingModal';
import TripPackages from '../components/TripPackages';
import TripReviews from '../components/TripReviews';
import { 
  MapPin, 
  Calendar, 
  Users, 
  ArrowLeft, 
  Share2, 
  Heart,
  CheckCircle,
  Wifi,
  Car,
  UtensilsCrossed,
  Waves,
  Mountain,
  TreePine,
  Coffee,
  Tv,
  Wind,
  Mail,
  Shield,
  LogIn,
  UserPlus,
  Globe,
  ExternalLink,
  User,
  Instagram,
  Star
} from 'lucide-react';

const AMENITY_ICONS = {
  wifi: Wifi,
  parking: Car,
  kitchen: UtensilsCrossed,
  beach: Waves,
  mountain: Mountain,
  nature: TreePine,
  coffee: Coffee,
  tv: Tv,
  airConditioning: Wind,
};

const AMENITY_LABELS = {
  wifi: 'WiFi',
  parking: 'Free Parking',
  kitchen: 'Kitchen',
  beach: 'Beach Access',
  mountain: 'Mountain View',
  nature: 'Nature Access',
  coffee: 'Coffee/Tea',
  tv: 'TV/Entertainment',
  airConditioning: 'Air Conditioning',
};

const TripDetails = () => {
  const { tripId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [trip, setTrip] = useState(null);
  const [hostProfile, setHostProfile] = useState(null);
  const [hostUser, setHostUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [guestCount, setGuestCount] = useState(1);
  const [booking, setBooking] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('booking'); // 'booking' or 'inquiry'
  const [modalLoading, setModalLoading] = useState(false);

  // Check if we're in a layout (AppLayout) or standalone
  const isInLayout = location.pathname.startsWith('/client/trips');

  useEffect(() => {
    const loadTripData = async () => {
      if (!tripId) return;
      try {
        const tripData = await getTrip(tripId);
        
        // For public route, only show published trips
        // For authenticated route, show all trips
        if (!isInLayout && tripData && tripData.status !== 'published') {
          setTrip(null);
          setLoading(false);
          return;
        }
        
        if (tripData) {
          setTrip({ ...tripData, id: tripId });
          
          // Load host profile and user data
          // For public route, only load if authenticated
          // For authenticated route, always load
          if (tripData.hostId && (isInLayout || user)) {
            try {
              const [profile, userData] = await Promise.all([
                getHostProfile(tripData.hostId).catch(() => null),
                getUserData(tripData.hostId).catch(() => null)
              ]);
              setHostProfile(profile);
              setHostUser(userData);
            } catch (error) {
              console.error('Error loading host data:', error);
            }
          }
        }
      } catch (error) {
        console.error('Error loading trip:', error);
      } finally {
        setLoading(false);
      }
    };
    loadTripData();
  }, [tripId, user, isInLayout]);

  // Don't set default selected date - let user select dates
  // Calendar will show today's month initially

  // Adjust guest count when package is selected to respect package limits
  useEffect(() => {
    if (selectedPackage) {
      const minGuests = selectedPackage.minGuests || 1;
      const maxGuests = selectedPackage.maxGuests || trip?.capacity || 10;
      
      if (guestCount < minGuests) {
        setGuestCount(minGuests);
      } else if (guestCount > maxGuests) {
        setGuestCount(maxGuests);
      }
    }
  }, [selectedPackage, trip?.capacity]);

  const handleInquirySubmit = async (formData) => {
    if (!user) {
      navigate('/login', { state: { returnTo: `/trips/${tripId}` } });
      return;
    }

    setModalLoading(true);
    try {
      const inquiryId = await createBooking({
        tripId,
        clientId: user.uid,
        hostId: trip.hostId,
        guestCount: formData.guestCount,
        selectedStartDate: formData.selectedStartDate,
        selectedEndDate: formData.selectedEndDate,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
        type: 'inquiry',
        status: 'pending',
        tripTitle: trip.title,
        tripLocation: trip.location,
      });
      alert('Inquiry sent successfully!');
      setShowModal(false);
      navigate(`/client/bookings/${inquiryId}`);
    } catch (error) {
      console.error('Error sending inquiry:', error);
      alert('Failed to send inquiry. Please try again.');
    } finally {
      setModalLoading(false);
    }
  };

  const handleBookingSubmit = async (formData) => {
    if (!user) {
      navigate('/login', { state: { returnTo: `/trips/${tripId}` } });
      return;
    }

    setModalLoading(true);
    try {
      const bookingId = await createBooking({
        tripId,
        clientId: user.uid,
        hostId: trip.hostId,
        guestCount: formData.guestCount,
        selectedStartDate: formData.selectedStartDate,
        selectedEndDate: formData.selectedEndDate,
        totalAmount: selectedPackage
          ? selectedPackage.price * formData.guestCount
          : (pricingModel === 'daily' && dailyPrice && formData.selectedStartDate && formData.selectedEndDate
              ? (() => {
                  const bookingNights = Math.ceil(Math.abs(formData.selectedEndDate - formData.selectedStartDate) / (1000 * 60 * 60 * 24));
                  return dailyPrice * bookingNights * formData.guestCount;
                })()
              : basePrice * formData.guestCount),
        startDate: trip.startDate,
        endDate: trip.endDate,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
        type: 'booking',
        status: 'pending',
        tripTitle: trip.title,
        tripLocation: trip.location,
      });
      alert('Booking request sent successfully!');
      setShowModal(false);
      navigate(`/client/bookings/${bookingId}`);
    } catch (error) {
      console.error('Error creating booking:', error);
      alert('Failed to create booking. Please try again.');
    } finally {
      setModalLoading(false);
    }
  };

  const handleBook = () => {
    if (!user) {
      navigate('/login', { state: { returnTo: `/trips/${tripId}` } });
      return;
    }
    setModalType('booking');
    setShowModal(true);
  };

  const handleInquiry = () => {
    if (!user) {
      navigate('/login', { state: { returnTo: `/trips/${tripId}` } });
      return;
    }
    setModalType('inquiry');
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50/20 via-white to-green-50/10">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-emerald-600 border-t-transparent"></div>
      </div>
    );
  }

  if (!trip) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50/20 via-white to-green-50/10">
        <div className="text-center">
          <p className="text-gray-500 text-lg mb-4">Listing not found</p>
          <Link
            to={isInLayout ? '/available-listings' : '/'}
            className="text-emerald-700 hover:text-emerald-800 font-medium"
          >
            Browse all listings
          </Link>
        </div>
      </div>
    );
  }

  // Format dates
  const startDate = trip?.startDate?.toDate ? trip.startDate.toDate() : trip?.startDate ? new Date(trip.startDate) : null;
  const endDate = trip?.endDate?.toDate ? trip.endDate.toDate() : trip?.endDate ? new Date(trip.endDate) : null;
  const photos = trip?.photos || [];
  const amenities = trip?.amenities || [];
  const highlights = trip?.highlights ? trip.highlights.split('\n').filter(h => h.trim()) : [];
  const packages = trip?.packages || [];
  const reviews = trip?.reviews || [];
  const pricingModel = trip?.pricingModel || 'base';
  const dailyPrice = trip?.dailyPrice || 0;
  const basePrice = trip?.basePrice || trip?.price || 0;

  // Calculate nights from selected date range
  const calculateNights = () => {
    if (selectedStartDate && selectedEndDate) {
      const diffTime = Math.abs(selectedEndDate - selectedStartDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    }
    // If package is selected but no date range, use package duration
    if (selectedPackage) {
      return selectedPackage.durationType === 'fixed' 
        ? selectedPackage.durationNights 
        : selectedPackage.durationMin || 1;
    }
    // For daily pricing without package or date range, use minimum stay
    if (pricingModel === 'daily' && selectedStartDate) {
      return trip?.minStay || 1;
    }
    return 1; // Base pricing is typically per person, not per night
  };

  // Calculate total price based on selected package OR daily/base pricing
  const calculateTotalPrice = () => {
    // If package is selected, use package pricing (package.price always wins, not dailyPrice)
    // Packages don't require date selection
    if (selectedPackage) {
      return selectedPackage.price * guestCount;
    }
    
    // If no package selected, require date range selection
    if (!selectedStartDate || !selectedEndDate) {
      return 0;
    }
    
    const nights = calculateNights();
    
    // If no package selected but date range is selected, use daily or base pricing
    if (pricingModel === 'daily' && dailyPrice) {
      // For daily pricing without package, calculate based on actual selected nights
      return dailyPrice * nights * guestCount;
    }
    
    // Base pricing
    return basePrice * guestCount;
  };

  const totalPrice = calculateTotalPrice();
  const nights = calculateNights();

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50/20 via-white to-green-50/10">
      {/* Header - only show if not in layout */}
      {!isInLayout && (
        <header className="border-b border-emerald-100 sticky top-0 bg-white/80 backdrop-blur-md z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center space-x-2 sm:space-x-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg overflow-hidden">
                  <img 
                    src="/logo192.png" 
                    alt="Spirara Logo" 
                    className="w-full h-full object-contain"
                  />
                </div>
                <span className="text-xl sm:text-2xl font-light text-gray-900">
                  Spirara<span className="text-emerald-700">.</span>
                </span>
              </Link>

              <div className="flex items-center gap-4">
                {user ? (
                  <>
                    {hasRole(user, 'client') && (
                      <Link
                        to="/available-listings"
                        className="hidden sm:inline-flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-emerald-700 transition-colors"
                      >
                        Dashboard
                      </Link>
                    )}
                    {hasRole(user, 'host') && (
                      <a
                        href={process.env.REACT_APP_HOST_APP_URL || 'https://host.spirara.com'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hidden sm:inline-flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-emerald-700 transition-colors"
                      >
                        Host Dashboard
                      </a>
                    )}
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      className="inline-flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-emerald-700 transition-colors"
                    >
                      <LogIn className="w-4 h-4" />
                      <span className="hidden sm:inline">Sign In</span>
                    </Link>
                    <Link
                      to="/signup"
                      className="inline-flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white px-5 py-2 rounded-lg transition-colors shadow-lg hover:shadow-xl font-medium"
                    >
                      <UserPlus className="w-4 h-4" />
                      <span>Sign Up</span>
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <div className="mb-6">
          {isInLayout ? (
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-4"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back</span>
            </button>
          ) : (
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-4"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to listings</span>
            </Link>
          )}
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h1 className="text-4xl font-light text-gray-900 mb-2">{trip.title}</h1>
              <div className="flex items-center gap-4 text-gray-600">
                <div className="flex items-center gap-1">
                  <MapPin className="w-4 h-4 text-emerald-600" />
                  <span className="text-sm">{trip.location}</span>
                </div>
                {hostProfile?.verified && (
                  <div className="flex items-center gap-1 text-emerald-600">
                    <Shield className="w-4 h-4" />
                    <span className="text-sm font-medium">Verified Host</span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button className="p-2 rounded-full hover:bg-gray-100 transition-colors">
                <Share2 className="w-5 h-5 text-gray-600" />
              </button>
              <button className="p-2 rounded-full hover:bg-gray-100 transition-colors">
                <Heart className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>
        </div>

        {/* Image Gallery */}
        {photos.length > 0 && (
          <div className="mb-8">
            {photos.length === 1 ? (
              <div className="rounded-2xl overflow-hidden">
                <img
                  src={photos[0]}
                  alt={trip.title}
                  className="w-full h-[500px] object-cover"
                />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2 rounded-2xl overflow-hidden">
                <div className="col-span-2 row-span-2">
                  <img
                    src={photos[selectedImageIndex]}
                    alt={`${trip.title} - Image ${selectedImageIndex + 1}`}
                    className="w-full h-[500px] object-cover cursor-pointer"
                    onClick={() => setSelectedImageIndex((selectedImageIndex + 1) % photos.length)}
                  />
                </div>
                {photos.slice(0, 4).map((photo, index) => (
                  <div
                    key={index}
                    className={`cursor-pointer ${index === selectedImageIndex ? 'ring-2 ring-emerald-500' : ''}`}
                    onClick={() => setSelectedImageIndex(index)}
                  >
                    <img
                      src={photo}
                      alt={`${trip.title} - Thumbnail ${index + 1}`}
                      className="w-full h-32 object-cover"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* About */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 p-8">
              <h2 className="text-2xl font-medium text-gray-900 mb-4">About this listing</h2>
              <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{trip.description}</p>
            </div>

            {/* Highlights */}
            {highlights.length > 0 && (
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 p-8">
                <h2 className="text-2xl font-medium text-gray-900 mb-4">Highlights</h2>
                <ul className="space-y-3">
                  {highlights.map((highlight, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{highlight.trim()}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Amenities */}
            {amenities.length > 0 && (
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 p-8">
                <h2 className="text-2xl font-medium text-gray-900 mb-6">What this place offers</h2>
                <div className="grid grid-cols-2 gap-4">
                  {amenities.map((amenityId) => {
                    const AmenityIcon = AMENITY_ICONS[amenityId] || CheckCircle;
                    const label = AMENITY_LABELS[amenityId] || amenityId;
                    return (
                      <div key={amenityId} className="flex items-center gap-3">
                        <AmenityIcon className="w-5 h-5 text-emerald-600" />
                        <span className="text-gray-700">{label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* What's Included & What to Bring */}
            {(trip.whatIncluded || trip.whatToBring) && (
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {trip.whatIncluded && (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-3">What's Included</h3>
                      <p className="text-gray-700 whitespace-pre-wrap">{trip.whatIncluded}</p>
                    </div>
                  )}
                  {trip.whatToBring && (
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-3">What to Bring</h3>
                      <p className="text-gray-700 whitespace-pre-wrap">{trip.whatToBring}</p>
                    </div>
                  )}
                </div>
              </div>
            )}


            {/* Instructor/Guide Info */}
            {(trip.instructorName || trip.instructorBio) && (
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 p-8">
                <h2 className="text-2xl font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <User className="w-6 h-6 text-emerald-600" />
                  <span>Meet Your Instructor/Guide</span>
                </h2>
                <div className="space-y-4">
                  {trip.instructorName && (
                    <h3 className="text-xl font-semibold text-gray-900">{trip.instructorName}</h3>
                  )}
                  {trip.instructorBio && (
                    <p className="text-gray-700 leading-relaxed">{trip.instructorBio}</p>
                  )}
                  <div className="flex flex-wrap gap-4 pt-4">
                    {trip.instructorEmail && (
                      <a
                        href={`mailto:${trip.instructorEmail}`}
                        className="flex items-center gap-2 text-emerald-700 hover:text-emerald-800 transition-colors"
                      >
                        <Mail className="w-4 h-4" />
                        <span className="text-sm">Email</span>
                      </a>
                    )}
                    {trip.instructorWebsite && (
                      <a
                        href={trip.instructorWebsite}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-emerald-700 hover:text-emerald-800 transition-colors"
                      >
                        <Globe className="w-4 h-4" />
                        <span className="text-sm">Website</span>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    {trip.instructorInstagram && (
                      <a
                        href={`https://instagram.com/${trip.instructorInstagram.replace('@', '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-emerald-700 hover:text-emerald-800 transition-colors"
                      >
                        <Instagram className="w-4 h-4" />
                        <span className="text-sm">{trip.instructorInstagram}</span>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Location Map */}
            {trip.location && (
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 p-8">
                <h2 className="text-2xl font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <MapPin className="w-6 h-6 text-emerald-600" />
                  <span>Where you'll be</span>
                </h2>
                <p className="text-gray-600 mb-4">{trip.location}</p>
                <div className="w-full h-[300px] rounded-xl overflow-hidden border border-gray-200 bg-gradient-to-br from-emerald-50/50 to-green-50/30 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-8xl mb-4">📍</div>
                    <p className="text-lg font-medium text-gray-900">{trip.location}</p>
                  </div>
                </div>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(trip.location)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-4 text-emerald-700 hover:text-emerald-800 font-medium text-sm transition-colors"
                >
                  <span>View on Google Maps</span>
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            )}

            {/* Reviews - Full Details */}
            <TripReviews reviews={reviews} />

          </div>

          {/* Booking Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Host Info Card */}
            {(user || isInLayout) && (hostUser || hostProfile) && (
              <div className="sticky top-8 bg-gradient-to-br from-emerald-50 via-white to-green-50/30 backdrop-blur-sm rounded-2xl shadow-lg border border-emerald-100/50 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <span>👋</span>
                  <span>Meet your host</span>
                </h2>
                <div className="flex items-center gap-4 mb-4">
                  {hostUser?.photoURL ? (
                    <img
                      src={hostUser.photoURL}
                      alt={hostUser.displayName || 'Host'}
                      className="w-16 h-16 rounded-full border-2 border-emerald-200 shadow-md"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-600 to-green-700 flex items-center justify-center text-white text-xl font-medium border-2 border-emerald-200 shadow-md">
                      {(hostUser?.displayName || hostUser?.email || 'H')[0].toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {hostUser?.displayName || 'Host'}
                    </h3>
                    {hostProfile?.verified && (
                      <div className="flex items-center gap-1 text-emerald-600 text-xs mt-1">
                        <Shield className="w-3 h-3" />
                        <span className="font-medium">Verified Host</span>
                      </div>
                    )}
                  </div>
                </div>
                
                {hostProfile?.bio && (
                  <p className="text-sm text-gray-600 mb-4 leading-relaxed">{hostProfile.bio}</p>
                )}

                <div className="space-y-2 pt-4 border-t border-emerald-100">
                  {hostUser?.email && (
                    <a
                      href={`mailto:${hostUser.email}`}
                      className="flex items-center gap-2 text-sm text-gray-700 hover:text-emerald-700 transition-colors"
                    >
                      <Mail className="w-4 h-4 text-emerald-600" />
                      <span>Contact Host</span>
                    </a>
                  )}
                  {hostProfile?.property?.website && (
                    <a
                      href={hostProfile.property.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-gray-700 hover:text-emerald-700 transition-colors"
                    >
                      <span className="text-base">🌐</span>
                      <span>Visit Website</span>
                      <ExternalLink className="w-3 h-3 ml-auto text-emerald-600" />
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Date Selection Card */}
            <div className="sticky top-8 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-emerald-600" />
                <span>Select arrival date</span>
              </h2>
              <DatePicker
                selectedStartDate={selectedStartDate}
                selectedEndDate={selectedEndDate}
                onDateSelect={({ startDate, endDate }) => {
                  setSelectedStartDate(startDate);
                  setSelectedEndDate(endDate);
                }}
                minDate={startDate}
                maxDate={endDate}
              />
              {selectedStartDate && (
                <div className="mt-4 p-3 bg-emerald-50 rounded-lg">
                  <p className="text-sm text-gray-700">
                    <span className="font-medium">Selected:</span>{' '}
                    {selectedStartDate.toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric',
                      year: 'numeric'
                    })}
                    {selectedEndDate && selectedEndDate.getTime() !== selectedStartDate.getTime() && (
                      <>
                        {' - '}
                        {selectedEndDate.toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric',
                          year: 'numeric'
                        })}
                        {' '}
                        <span className="text-emerald-700 font-medium">
                          ({nights} {nights === 1 ? 'night' : 'nights'})
                        </span>
                      </>
                    )}
                  </p>
                </div>
              )}
            </div>

            {/* Packages Card */}
            <TripPackages 
              packages={packages}
              selectedPackage={selectedPackage}
              onPackageSelect={(pkg, index) => {
                if (pkg === null) {
                  setSelectedPackage(null);
                } else {
                  setSelectedPackage({ ...pkg, index });
                }
              }}
              selectedStartDate={selectedStartDate}
              selectedEndDate={selectedEndDate}
              guestCount={guestCount}
              pricingModel={pricingModel}
              dailyPrice={dailyPrice}
              basePrice={basePrice}
              minStay={trip?.minStay || 1}
            />

            {/* Booking Card */}
            <div className="sticky top-8 bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200/50 p-6">
              <div className="mb-6">
                {(selectedPackage || (selectedStartDate && selectedEndDate)) ? (
                  <>
                    <div className="flex items-baseline gap-2 mb-2">
                      <span className="text-3xl font-semibold text-gray-900">
                        ${selectedPackage 
                          ? selectedPackage.price 
                          : (pricingModel === 'daily' && dailyPrice ? dailyPrice : basePrice)}
                      </span>
                      <span className="text-gray-600">
                        {selectedPackage 
                          ? 'per person'
                          : (pricingModel === 'daily' && dailyPrice ? 'per person/night' : 'per person')}
                      </span>
                    </div>
                    {selectedPackage && (
                      <p className="text-sm text-gray-600">
                        Package: {selectedPackage.name}
                      </p>
                    )}
                  </>
                ) : (
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-3xl font-semibold text-gray-400">
                      $0
                    </span>
                    <span className="text-gray-500">
                      Select dates to see pricing
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    <span>
                      {startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} -{' '}
                      {endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Number of Guests
                  </label>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        const minGuests = selectedPackage?.minGuests || 1;
                        setGuestCount(Math.max(minGuests, guestCount - 1));
                      }}
                      disabled={guestCount <= (selectedPackage?.minGuests || 1)}
                      className="w-10 h-10 rounded-lg border border-gray-300 flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="text-lg">−</span>
                    </button>
                    <span className="text-lg font-medium text-gray-900 w-12 text-center">{guestCount}</span>
                    <button
                      type="button"
                      onClick={() => {
                        const maxGuests = selectedPackage?.maxGuests || trip.capacity;
                        setGuestCount(Math.min(maxGuests, guestCount + 1));
                      }}
                      disabled={guestCount >= (selectedPackage?.maxGuests || trip.capacity)}
                      className="w-10 h-10 rounded-lg border border-gray-300 flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="text-lg">+</span>
                    </button>
                    <span className="text-sm text-gray-600 ml-2">
                      {selectedPackage 
                        ? `(${selectedPackage.minGuests}-${selectedPackage.maxGuests} guests)`
                        : `(max ${trip.capacity})`}
                    </span>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-gray-700">Total</span>
                    <span className="text-2xl font-semibold text-gray-900">
                      ${totalPrice.toFixed(2)}
                    </span>
                  </div>
                  {(selectedPackage || (selectedStartDate && selectedEndDate)) && (
                    <div className="text-xs text-gray-500 mb-2 space-y-1">
                      {selectedPackage ? (
                        <>
                          {selectedStartDate && selectedEndDate && (
                            <p>
                              {nights} {nights === 1 ? 'night' : 'nights'} × {guestCount} {guestCount === 1 ? 'guest' : 'guests'}
                            </p>
                          )}
                          <p>${selectedPackage.price} per person</p>
                        </>
                      ) : pricingModel === 'daily' && dailyPrice && selectedEndDate ? (
                        <>
                          <p>
                            {nights} {nights === 1 ? 'night' : 'nights'} × {guestCount} {guestCount === 1 ? 'guest' : 'guests'}
                          </p>
                          <p>${dailyPrice} per person/night</p>
                        </>
                      ) : selectedStartDate && selectedEndDate ? (
                        <p>
                          {guestCount} {guestCount === 1 ? 'guest' : 'guests'} × ${basePrice} per person
                        </p>
                      ) : null}
                    </div>
                  )}
                  {!selectedPackage && (!selectedStartDate || !selectedEndDate) && (
                    <p className="text-xs text-gray-500 mb-2">
                      Please select dates to see pricing
                    </p>
                  )}
                  <div className="space-y-3">
                    <button
                      onClick={handleBook}
                      disabled={!user && isInLayout}
                      className="w-full bg-emerald-700 hover:bg-emerald-800 text-white py-4 px-6 rounded-lg font-medium shadow-lg shadow-emerald-200/50 hover:shadow-xl hover:shadow-emerald-300/50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {!user ? 'Sign in to Book' : 'Request to Book'}
                    </button>
                    {user && (
                      <button
                        onClick={handleInquiry}
                        className="w-full border-2 border-emerald-700 text-emerald-700 hover:bg-emerald-50 py-4 px-6 rounded-lg font-medium transition-all duration-300"
                      >
                        Send Inquiry
                      </button>
                    )}
                  </div>
                  {!user && (
                    <p className="text-xs text-gray-500 text-center mt-2">
                      You'll need to sign in to book this listing
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200 space-y-2">
                <div className="flex items-center justify-between text-sm text-gray-600">
                  <span>Capacity</span>
                  <span className="font-medium">{trip.capacity} {trip.capacity === 1 ? 'guest' : 'guests'}</span>
                </div>
                {trip.minStay && (
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <span>Minimum Stay</span>
                    <span className="font-medium">{trip.minStay} {trip.minStay === 1 ? 'night' : 'nights'}</span>
                  </div>
                )}
                {trip.maxStay && (
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <span>Maximum Stay</span>
                    <span className="font-medium">{trip.maxStay} {trip.maxStay === 1 ? 'night' : 'nights'}</span>
                  </div>
                )}
                {trip.bookingWindow && (
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <span>Booking Window</span>
                    <span className="font-medium">{trip.bookingWindow} {trip.bookingWindow === 1 ? 'day' : 'days'} in advance</span>
                  </div>
                )}
                {trip.instantBooking && (
                  <div className="flex items-center justify-between text-sm text-emerald-600">
                    <span>Instant Booking</span>
                    <span className="font-medium flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" />
                      Available
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Inquiry/Booking Modal */}
      {trip && (
        <InquiryBookingModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          trip={trip}
          selectedStartDate={selectedStartDate}
          selectedEndDate={selectedEndDate}
          guestCount={guestCount}
          onInquirySubmit={handleInquirySubmit}
          onBookingSubmit={handleBookingSubmit}
          loading={modalLoading}
          modalType={modalType}
        />
      )}
    </div>
  );
};

export default TripDetails;
